import { Filesystem, Directory } from '@capacitor/filesystem'
import { Capacitor } from '@capacitor/core'
import { offlineAttachmentDb, type CachedAttachment } from './offlineAttachmentDb'
import { toAttachmentFilePath, ATTACHMENTS_DIR } from './attachmentFilePath'
import { isNativePlatform } from '@/lib/platform/platformDetection'
import { mediaCacheKey } from '@/lib/media/mediaCacheKey'

const SEVEN_DAYS_MS = 1000 * 60 * 60 * 24 * 7
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024 // 10 MB — limit for blob→base64 in WKWebView
const RESTORE_BATCH_SIZE = 50

/**
 * Module-level map: relative URL → WebView-serveable URL. Sync O(1) lookup.
 *
 * Every key, here and in the Dexie index (`url`), is `mediaCacheKey(relativeUrl)` — the URL
 * without its signature — so a re-signed URL for a file already on disk still resolves to it,
 * and no signed URL is ever persisted.
 */
const urlMap = new Map<string, string>()

/** Cached base URI for the attachments directory (resolved once). */
let cachedBaseWebUrl: string | null = null

/** Guards against concurrent cacheAttachmentBlob / clearCachedAttachments races. */
let clearInProgress: Promise<void> | null = null

/**
 * Write generation for this page's attachment caches (AUTHZ-02 E2 account boundary — invariant
 * in `@/lib/media/mediaAccountBoundary`). A download captures it before its request leaves
 * (`getAttachmentWriteGeneration`), and every write — native file, Dexie row, urlMap entry, and
 * the web prefetch's `media-uploads-cache` put — is refused once it has moved on. Moved on by
 * `invalidateAttachmentWrites` (first step of the account purge) and by `clearCachedAttachments`,
 * so bytes fetched for the previous user can never be committed after their caches were wiped.
 */
let writeGeneration = 0

export function getAttachmentWriteGeneration(): number {
  return writeGeneration
}

export function invalidateAttachmentWrites(): void {
  writeGeneration += 1
}

/**
 * Persisted hint: have we created the native attachments directory on this
 * install? Capacitor's native bridge `console.error`s EVERY failed Filesystem
 * plugin call when logging is enabled — even when the JS promise rejection is
 * caught — so a `mkdir` on an existing dir ("already exists") or an `rmdir` on
 * a missing dir ("directory does not exist") surfaces as a noisy error we can't
 * suppress after the fact. A probe (`stat`/`readdir`) doesn't help either:
 * probing a missing path fails and gets logged too. The only quiet path is to
 * not make a call that will fail. This flag lets us skip `mkdir` when the dir
 * already exists and skip `rmdir` when there's nothing to remove (the common
 * "user never cached anything" cold start), without touching the filesystem.
 */
const DIR_READY_KEY = 'offlineAttachmentsDirReady'

function markAttachmentsDirReady(): void {
  try {
    localStorage.setItem(DIR_READY_KEY, '1')
  } catch {
    // localStorage unavailable — the flag is a best-effort log-noise optimisation.
  }
}

function clearAttachmentsDirReadyFlag(): void {
  try {
    localStorage.removeItem(DIR_READY_KEY)
  } catch {
    // ignore — see markAttachmentsDirReady
  }
}

/**
 * True if we may have created the attachments dir this install. Defaults to
 * true when the flag can't be read, so cleanup still runs — correctness
 * (don't leak orphaned blobs) wins over quiet logs when storage is flaky.
 */
function attachmentsDirMaybeExists(): boolean {
  try {
    return localStorage.getItem(DIR_READY_KEY) === '1'
  } catch {
    return true
  }
}

/**
 * Sync lookup for a cached attachment URL. Called from `getAssetUrl()`.
 * Returns the WebView-serveable URL if cached, otherwise undefined.
 */
export function getCachedBlobUrl(relativeUrl: string): string | undefined {
  return urlMap.get(mediaCacheKey(relativeUrl))
}

/**
 * Returns the number of attachments currently cached.
 * Native: reads the in-memory urlMap (populated from Capacitor Filesystem).
 * Web: reads from the Dexie index (also written on web via
 *      `recordWebAttachment` after a successful prefetch).
 */
export async function getCachedAttachmentCount(): Promise<number> {
  if (isNativePlatform()) return urlMap.size

  try {
    return await offlineAttachmentDb.attachments.count()
  } catch {
    return 0
  }
}

/**
 * Web-only: record metadata for an attachment that was just fetched and
 * cached by the Service Worker. The SW does the actual caching; we only
 * track the row so per-stage size and counts are computable without
 * walking the SW cache (which is unreliable: rotating ?token query
 * strings, async eviction, dev-mode SW quirks).
 *
 * `filePath` is meaningless on web (the file lives in the SW cache, not
 * the filesystem) so we store the relative URL there as a harmless
 * non-empty value.
 */
export async function recordWebAttachment(
  relativeUrl: string,
  stageId: string,
  byteLength: number,
): Promise<void> {
  const key = mediaCacheKey(relativeUrl)
  try {
    await offlineAttachmentDb.attachments.put({
      url: key,
      filePath: key,
      cachedAt: Date.now(),
      stageId,
      byteLength,
    })
  } catch {
    // Best-effort — failure here just means the size display under-counts.
  }
}

/**
 * Web-only: drop Dexie metadata rows for a stage. Mirrors the native
 * `clearAttachmentsByStage` shape, but doesn't touch the filesystem
 * (web has no filesystem; SW cache cleanup happens elsewhere).
 */
export async function clearWebAttachmentMetadataByStage(stageId: string): Promise<void> {
  try {
    await offlineAttachmentDb.attachments.where('stageId').equals(stageId).delete()
  } catch {
    // Best-effort — leftover rows would just over-count size estimates.
  }
}

/**
 * Resolve the WebView-serveable base URL for the attachments directory.
 * Calls Filesystem.getUri once and caches the result.
 */
async function resolveBaseWebUrl(): Promise<string> {
  if (cachedBaseWebUrl) return cachedBaseWebUrl

  // Ensure the directory exists. Skip mkdir once we know it's there: calling
  // mkdir on an existing dir makes Capacitor log an "already exists" error
  // (even with recursive:true, and even though we catch the rejection).
  // `cacheAttachmentBlob` writes with recursive:true, so the dir self-heals if
  // the OS evicts the cache — getUri below only resolves a path and doesn't
  // require the dir to exist.
  if (!attachmentsDirMaybeExists()) {
    try {
      await Filesystem.mkdir({
        path: ATTACHMENTS_DIR,
        directory: Directory.Cache,
        recursive: true,
      })
    } catch {
      // Directory may already exist — safe to ignore
    }
    markAttachmentsDirReady()
  }

  const { uri } = await Filesystem.getUri({
    path: ATTACHMENTS_DIR,
    directory: Directory.Cache,
  })

  cachedBaseWebUrl = Capacitor.convertFileSrc(uri)
  return cachedBaseWebUrl
}

/**
 * Build a WebView-serveable URL from a file path using the cached base URL.
 * Pure string concatenation — no async I/O.
 */
function buildWebUrl(baseWebUrl: string, filePath: string): string {
  // filePath is "attachments/some_file.jpg", strip the dir prefix
  const fileName = filePath.slice(ATTACHMENTS_DIR.length + 1)
  const cleanBase = baseWebUrl.endsWith('/') ? baseWebUrl : `${baseWebUrl}/`
  return `${cleanBase}${fileName}`
}

/**
 * Yield to the main thread so the JS engine can run GC.
 */
function yieldToMain(): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, 0))
}

/**
 * Convert a Blob to a base64-encoded string using FileReader.
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = reader.result as string
      // Strip the "data:...;base64," prefix
      const base64 = dataUrl.split(',')[1]
      if (base64) {
        resolve(base64)
      } else {
        reject(new Error('Failed to extract base64 from data URL'))
      }
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

/**
 * Store an attachment on the native filesystem and index it in Dexie.
 * Populates the in-memory map with a WebView-serveable URL.
 *
 * Rejects blobs larger than MAX_FILE_SIZE_BYTES to avoid the blob→base64
 * conversion exhausting WKWebView's ~80-120 MB memory budget.
 *
 * Ensures atomicity: if the Dexie write fails after the file was written,
 * the orphaned file is cleaned up.
 *
 * `generation` is the write generation the caller captured BEFORE fetching the
 * blob (default: the current one). The write is refused — `false`, nothing left
 * behind — if the generation moves on before the file, the Dexie row or the
 * urlMap entry is committed: those bytes belong to whoever was signed in when
 * the request left. Resolves `true` once all three are written.
 */
export async function cacheAttachmentBlob(
  relativeUrl: string,
  blob: Blob,
  stageId: string,
  generation: number = writeGeneration,
): Promise<boolean> {
  if (blob.size > MAX_FILE_SIZE_BYTES) {
    throw new Error(
      `Attachment too large to cache (${blob.size} bytes, limit ${MAX_FILE_SIZE_BYTES})`,
    )
  }

  // Wait for any in-progress clear to finish before writing
  if (clearInProgress) {
    await clearInProgress
  }
  if (generation !== writeGeneration) return false

  const key = mediaCacheKey(relativeUrl)
  const filePath = toAttachmentFilePath(key)
  const base64Data = await blobToBase64(blob)
  const deleteFile = () =>
    Filesystem.deleteFile({ path: filePath, directory: Directory.Cache }).catch(() => {})

  await Filesystem.writeFile({
    path: filePath,
    data: base64Data,
    directory: Directory.Cache,
    recursive: true,
  })
  // writeFile(recursive) just created (or confirmed) the attachments dir — record
  // that so resolveBaseWebUrl below, and future cold starts, skip the mkdir.
  markAttachmentsDirReady()

  if (generation !== writeGeneration) {
    void deleteFile()
    return false
  }

  try {
    await offlineAttachmentDb.attachments.put({
      url: key,
      filePath,
      cachedAt: Date.now(),
      stageId,
      byteLength: blob.size,
    })
  } catch (error) {
    // Clean up orphaned file if Dexie write fails
    void deleteFile()
    throw error
  }

  const baseWebUrl = await resolveBaseWebUrl()
  // The clear may have run while the row was being written, after its own Dexie clear.
  if (generation !== writeGeneration) {
    await offlineAttachmentDb.attachments.delete(key).catch(() => {})
    void deleteFile()
    return false
  }
  urlMap.set(key, buildWebUrl(baseWebUrl, filePath))
  return true
}

/**
 * Returns all cached-attachment rows for a given stage.
 * Used by the offline-cache settings page to compute per-stage size and
 * to drive the per-stage clear flow.
 */
export async function getAttachmentsByStage(stageId: string): Promise<CachedAttachment[]> {
  try {
    return await offlineAttachmentDb.attachments.where('stageId').equals(stageId).toArray()
  } catch {
    return []
  }
}

/**
 * Sum the byte sizes of cached attachments. With no argument, returns the
 * total across all stages. With a stageId, returns just that stage's sum.
 */
export async function getAttachmentsTotalSize(stageId?: string): Promise<number> {
  try {
    const rows = stageId
      ? await offlineAttachmentDb.attachments.where('stageId').equals(stageId).toArray()
      : await offlineAttachmentDb.attachments.toArray()
    let total = 0
    for (const row of rows) {
      total += row.byteLength ?? 0
    }
    return total
  } catch {
    return 0
  }
}

/**
 * Delete every cached attachment belonging to a stage: removes Dexie rows,
 * unlinks files from the filesystem, and prunes the in-memory urlMap.
 * Other stages' attachments stay intact.
 */
export async function clearAttachmentsByStage(stageId: string): Promise<void> {
  // Wait for any in-progress full clear to finish first to avoid racing.
  if (clearInProgress) {
    await clearInProgress
  }

  const rows = await getAttachmentsByStage(stageId)
  if (rows.length === 0) return

  // Best-effort filesystem cleanup; swallow per-file failures so a single
  // missing file doesn't block the rest of the cleanup.
  await Promise.all(
    rows.map(row =>
      Filesystem.deleteFile({ path: row.filePath, directory: Directory.Cache }).catch(() => {}),
    ),
  )

  await offlineAttachmentDb.attachments
    .where('stageId')
    .equals(stageId)
    .delete()
    .catch(() => {})

  for (const row of rows) {
    urlMap.delete(mediaCacheKey(row.url))
  }
}

/**
 * Restore cached attachments from Dexie on cold start.
 * Reads the lightweight index (no blobs), resolves the base cache URI once,
 * and builds all WebView URLs via string concatenation.
 *
 * Only runs on native platforms. Returns the number of restored entries.
 */
export async function restoreCachedAttachments(): Promise<number> {
  if (!isNativePlatform()) return 0

  urlMap.clear()

  const cutoff = Date.now() - SEVEN_DAYS_MS
  await offlineAttachmentDb.attachments.where('cachedAt').below(cutoff).delete()

  const totalCount = await offlineAttachmentDb.attachments.count()
  if (totalCount === 0) {
    // Empty index = no row pointers to any file = anything left on disk is
    // orphaned (e.g. left over after a Dexie schema bump cleared the table).
    // Drop the directory so storage doesn't accumulate stale blobs.
    await removeAttachmentsDir().catch(() => {})
    return 0
  }

  let baseWebUrl: string
  try {
    baseWebUrl = await resolveBaseWebUrl()
  } catch {
    // Cannot resolve filesystem URI — fall back to online-only
    return 0
  }

  let offset = 0
  let count = 0

  while (true) {
    const batch = await offlineAttachmentDb.attachments
      .offset(offset)
      .limit(RESTORE_BATCH_SIZE)
      .toArray()

    if (batch.length === 0) break

    for (const entry of batch) {
      urlMap.set(mediaCacheKey(entry.url), buildWebUrl(baseWebUrl, entry.filePath))
      count++
    }

    offset += RESTORE_BATCH_SIZE
    await yieldToMain()
  }

  return count
}

/**
 * Clear the in-memory map, delete all Dexie entries, and remove the
 * attachments directory from the native filesystem.
 * Called on logout. Map clear is synchronous; filesystem/Dexie cleanup
 * is awaited via `clearInProgress` so concurrent `cacheAttachmentBlob`
 * calls don't race against the directory deletion.
 */
export function clearCachedAttachments(): Promise<void> {
  invalidateAttachmentWrites()
  urlMap.clear()
  cachedBaseWebUrl = null

  const cleanup = Promise.all([
    offlineAttachmentDb.attachments.clear().catch(() => {
      // Best-effort cleanup — DB entries will expire via TTL on next restore
    }),
    removeAttachmentsDir().catch(() => {
      // Best-effort cleanup — OS may have already cleared it
    }),
  ]).then(() => {
    clearInProgress = null
  })

  clearInProgress = cleanup
  // Callers (logout, harness CachesPanel, `fullCacheNuke`) can await
  // this to know when Dexie + filesystem cleanup actually finishes.
  // Pre-existing fire-and-forget callers can keep ignoring the return.
  return cleanup
}

async function removeAttachmentsDir(): Promise<void> {
  // Skip entirely if we never created the dir. On the common "nothing cached"
  // cold start it doesn't exist, and rmdir-on-missing logs a noisy
  // (caught-but-still-printed) Filesystem error every launch — the dominant
  // source of the reported noise. The previous mkdir-before-rmdir guard only
  // traded that for an equally noisy mkdir-on-existing (and pointlessly created
  // a directory just to delete it).
  if (!attachmentsDirMaybeExists()) return

  // recursive:true so a non-empty dir is removed in one call.
  try {
    await Filesystem.rmdir({
      path: ATTACHMENTS_DIR,
      directory: Directory.Cache,
      recursive: true,
    })
    // Removed — forget the dir so future cold starts skip the rmdir.
    clearAttachmentsDirReadyFlag()
  } catch (error) {
    // Only clear the flag on a genuine "directory is already gone" failure
    // (stale flag after the OS evicted the cache) — otherwise we'd keep
    // re-attempting and re-logging that forever. A REAL failure (e.g. the dir
    // is busy during a concurrent prefetch → "couldn't be removed") must stay
    // retryable: keep the flag set so the next logout / cold start tries again
    // rather than leaking cached files on disk. Re-throw so the caller's
    // best-effort catch swallows it without the flag being cleared.
    if (isMissingDirectoryError(error)) {
      clearAttachmentsDirReadyFlag()
      return
    }
    throw error
  }
}

/**
 * Capacitor reports a missing rmdir target with a "...does not exist." message
 * on both iOS (`FilesystemError.fileNotFound`) and Android
 * (`IONFILEExceptions.DoesNotExist`). Treat that as "already gone" rather than a
 * real, retryable failure.
 */
function isMissingDirectoryError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return /does not exist|no such file|not found/i.test(message)
}
